import React, {
	ReactNode, useEffect, createContext, useMemo, useCallback,
} from 'react'
// hooks

import { Token } from 'src/@types/token'
import useCustomizedToken from 'src/hooks/useCustomizedToken'
import { CustomizedToken } from 'src/@types/customizedToken'
import { find, includes, isNil } from 'lodash'
import { IUserNft } from 'src/types/tokens'
import useLocalStorage from '../hooks/useLocalStorage'
// utils

import { useDispatch, useSelector } from '../redux/store'
import useAuth from '../hooks/useAuth'
import { getTokens } from '../redux/slices/token'
import activateFirebaseEmulator, { getFirebase } from '../services/firebase'

// @type

// ----------------------------------------------------------------------

type PfpContextProps = {
  id: string | null
  changePrimaryPfp: (id: string) => void
	tokens: Array<Token>
	pfp: CustomizedToken | null
}

const initialState: PfpContextProps = {
	id: null,
	pfp: null,
	changePrimaryPfp: (id:string) => {},
	tokens: [],
}

const PfpContext = createContext(initialState)

// ----------------------------------------------------------------------

const isSelectedIdInTokensList = (userTokens:Array<IUserNft>, id: string): boolean => (!!find(userTokens, (token) => token.id === id))

// ----------------------------------------------------------------------

type PfpProviderProps = {
  children: ReactNode
};

function PfpProvider({ children }: PfpProviderProps) {
	const { tokens: userTokens } = useAuth()

	const { tokens } = useSelector((state) => state.token)

	const dispatch = useDispatch()

	const [id, setId] = useLocalStorage('activeCustomPfp', null)

	const { customizedToken } = useCustomizedToken(id)

	useEffect(() => {
		dispatch(getTokens(userTokens))
	}, [dispatch, userTokens])

	const changePrimaryPfp = useCallback((id:string) => {
		setId(id)
	}, [setId])

	useEffect(() => {
		if (userTokens.length !== 0 && (isNil(id) || !isSelectedIdInTokensList(userTokens, id))) {
			changePrimaryPfp(userTokens[0].id)
		}
	}, [userTokens, id, changePrimaryPfp])

	const value = useMemo(() => {
		const a: PfpContextProps = {
			changePrimaryPfp,
			id,
			tokens,
			pfp: customizedToken,
		}

		return a
	}, [tokens, id, changePrimaryPfp, customizedToken])

	return (
		<PfpContext.Provider
			value={value}
		>
			{children}
		</PfpContext.Provider>
	)
}

export { PfpProvider, PfpContext }
